import {
  Button,
  Chip,
  Divider,
  Paper,
  TextField,
  Typography
} from '@material-ui/core';
import { Theme, createStyles, makeStyles } from '@material-ui/core/styles';
import {
  ClearRounded,
  PlaylistAddRounded,
  UpdateRounded
} from '@material-ui/icons';
import { Autocomplete } from '@material-ui/lab';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import { RootState } from '../../../app/store';
import { useAppDispatch } from '../../../hooks/useAppRudux';
import Breadcrumb from '../../Breadcrumb';
import loadingSlice from '../../Loading/loadingSlice';
import {
  WorkflowStep,
  getWorkflowStepsAsync
} from '../WorkflowStep/workflowStepSlice';
import {
  createWorkflowAsync,
  getWorkflowAsync,
  updateWorkflowAsync
} from './workflowSlice';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      margin: theme.spacing(1.5, 0, 12, 0)
    },
    title: {
      margin: theme.spacing(1.5, 0, 2, 0),
      [theme.breakpoints.down('xs')]: {
        textAlign: 'center',
        margin: theme.spacing(3.5, 0, 4, 0)
      }
    },
    paper: {
      padding: theme.spacing(2),
      marginBottom: theme.spacing(2),
      borderRadius: '16px'
    },
    divider: {
      margin: theme.spacing(0.5, 0, 2, 0),
      width: 32
    },
    columnFullWidth: {
      width: '100%',
      marginBottom: theme.spacing(2)
    }
  })
);

interface CreateEditWorkflow {
  id: number | null;
  name: string | null;
  workflowSteps: WorkflowStep[];
}

export default function WorkflowCreateEdit() {
  const classes = useStyles();
  const history = useHistory();
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const { selectedWorkflow } = useSelector(
    (state: RootState) => state.workflows
  );
  const { data: workflowSteps } = useSelector(
    (state: RootState) => state.workflowSteps
  );

  const params = useParams<{ workflowId: string }>();
  const workflowId = Number.parseInt(params.workflowId, 10);
  const isNewPage = !Boolean(workflowId);

  const [workflow, setWorkflow] = useState<CreateEditWorkflow>({
    id: null,
    name: null,
    workflowSteps: []
  });
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    if (!isNewPage && selectedWorkflow) {
      setWorkflow({
        ...workflow,
        id: selectedWorkflow.id,
        name: selectedWorkflow.name,
        workflowSteps: selectedWorkflow.workflowSteps
      });
    }
  }, [selectedWorkflow]);

  useEffect(() => {
    if (!isNewPage) dispatch(getWorkflowAsync(workflowId));
    dispatch(getWorkflowStepsAsync());
  }, []);

  function onChangeField(event: React.ChangeEvent<HTMLInputElement>) {
    const { name, value } = event.target;
    setWorkflow({
      ...workflow,
      [name]: value === '' ? null : value
    });
  }

  async function onSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    try {
      setLoading(true);
      if (workflow.name) {
        if (isNewPage) {
          // create workflow
          const createWorkflow = {
            name: workflow.name,
            workflowStepIds: workflow.workflowSteps.map(
              (workflowStep) => workflowStep.id
            )
          };
          await dispatch(createWorkflowAsync(createWorkflow)).unwrap();
          dispatch(
            loadingSlice.actions.success({
              message: t('workflowCreateEdit.messages.createSuccess')
            })
          );
        } else {
          // update workflow
          const updateWorkflow = {
            id: workflow.id,
            name: workflow.name,
            workflowStepIds: workflow.workflowSteps.map(
              (workflowStep) => workflowStep.id
            )
          };
          await dispatch(updateWorkflowAsync(updateWorkflow)).unwrap();
          dispatch(
            loadingSlice.actions.success({
              message: t('workflowCreateEdit.messages.updateSuccess')
            })
          );
        }
      } else {
        throw new Error(t('workflowCreateEdit.errors.general') as string);
      }
      setTimeout(() => {
        history.goBack();
      }, 1000);
    } catch {
      setLoading(false);
    }
  }

  return (
    <div className={classes.root}>
      <Breadcrumb
        name={isNewPage ? 'masterDataCreate' : 'masterDataSubEdit'}
        data={{
          masterDataName: t('workflowCreateEdit.breadcrumb.masterDataName'),
          masterDataRef: 'workflows',
          masterDataSubName: selectedWorkflow?.name,
          masterDataSubRef: selectedWorkflow?.id
        }}
      />
      <Typography className={classes.title} component="p" variant="h4">
        {isNewPage ? t('workflowCreateEdit.title.new') : t('workflowCreateEdit.title.edit')}
      </Typography>
      <form onSubmit={onSubmit}>
        <Paper className={classes.paper}>
          <Typography variant="subtitle1" gutterBottom>
            {t('workflowCreateEdit.sections.information')}
          </Typography>
          <Divider className={classes.divider} />
          <TextField
            className={classes.columnFullWidth}
            id="workflow-name"
            label={t('workflowCreateEdit.labels.name')}
            size="small"
            variant="outlined"
            name="name"
            fullWidth
            value={workflow.name ? workflow.name : ''}
            onChange={onChangeField}
            required
          />
          <Autocomplete
            id="workflowSteps"
            size="small"
            multiple
            disableCloseOnSelect
            options={workflowSteps}
            getOptionLabel={(option) => option?.name}
            renderTags={(tagValue, getTagProps) =>
              tagValue.map((option, index) => (
                <Chip
                  label={option.name}
                  {...getTagProps({ index })}
                  variant="outlined"
                  size="small"
                  deleteIcon={<ClearRounded />}
                />
              ))
            }
            renderInput={(params) => (
              <TextField {...params} variant="outlined" label={t('workflowCreateEdit.labels.workflowStep')} />
            )}
            value={workflow.workflowSteps}
            getOptionSelected={(option, value) => option.id === value?.id}
            onChange={(event, newValue) =>
              setWorkflow({
                ...workflow,
                workflowSteps: newValue
              })
            }
          />
        </Paper>
        <Button
          type="submit"
          variant="contained"
          color="primary"
          size="large"
          fullWidth
          startIcon={isNewPage ? <PlaylistAddRounded /> : <UpdateRounded />}
          disabled={loading}
        >
          {isNewPage ? t('workflowCreateEdit.buttons.add') : t('workflowCreateEdit.buttons.update')}
        </Button>
      </form>
    </div>
  );
}
